import {  registerBrick } from 'olympe';
import {ReactBrick, useProperty} from "@olympeio/core";
import {combineLatest, map} from "rxjs";
import React, { useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import styled from "@emotion/styled";
import {cacheManager} from '@olympeio-extensions/commons';
import {format} from 'date-fns';


export default class Calendar extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
            $.observe('on event click', false).pipe(map(a => a === null ? null : [$.runner(a), a.getInputs()])),
            $.observe('on slot creation', false).pipe(map(a => a === null ? null : [$.runner(a), a.getInputs()])),
            $.observe('on new dates', false).pipe(map(a => a === null ? null : [$.runner(a), a.getInputs()])),
        ]);
    }
    /**
     * @override
     */
    static getReactComponent($) {

        return (props) => {
            const [hidden, onEventClick$, onSlotCreation$, onNewDates$] = props.values;
            const translations = JSON.parse(cacheManager().getByKey('translations') ?? '{}');

            const calendarRef = useRef(null);
            const width = useProperty($, 'Width');
            const events = useProperty($, 'events');
            const locale = useProperty($, 'locale');

            const handleEventClick = (info) => {
                if (onEventClick$) {
                    const event = JSON.parse(JSON.stringify(info.event));
                    event.start = new Date(event.start);
                    event.end = new Date(event.end);
                    const [ctrlFlowInput, eventInput] = onEventClick$[1];
                    onEventClick$[0]
                        .set(eventInput, event)
                        .trigger(ctrlFlowInput);
                }
            };

            const handleDateSelect = (selectInfo) => {
                if (onSlotCreation$) {
                    // Set 23h59 when finishing on midnight to avoid issues
                    let end = selectInfo.end;
                    const endStr = format(end, 'HH:mm');
                    if (endStr === '00:00') {
                        end = new Date(end.getTime() - 60000);
                    }
                    const [ctrlFlowInput, fromInput, toInput] = onSlotCreation$[1];
                    onSlotCreation$[0]
                        .set(fromInput, selectInfo.start)
                        .set(toInput, end)
                        .trigger(ctrlFlowInput);
                }
                // Clear the selection
                selectInfo.view.calendar.unselect();
            };

            const handleDateSet = (info) => {
                if ($.get('last-start-date') && ($.get('last-start-date') <= info.start.getTime() && $.get('last-end-date') >= info.end.getTime())) {
                    // Do nothing; the scope reduced or is the same
                } else {
                    if (onNewDates$) {
                        const [ctrlFlowInput, fromInput, toInput] = onNewDates$[1];
                        onNewDates$[0]
                            .set(fromInput, info.start)
                            .set(toInput, info.end)
                            .trigger(ctrlFlowInput);

                        $.set('last-start-date', info.start.getTime());
                        $.set('last-end-date', info.end.getTime());
                    }
                }
            };

            // Initialize FullCalendar on mount
            useEffect(() => {
                if (calendarRef.current) {
                    if (width) {
                        const calendarApi = calendarRef.current.getApi();
                        calendarApi.changeView(width > 756 ? 'timeGridWeek' : 'timeGridDay');
                    }
                    calendarRef.current.getApi().render();
                }
            }, [width]);

            const renderEventContent = (eventInfo) => {
                return (
                    <div className={'flex justify-center items-center text-center w-full h-full bg-opacity-20 rounded border'
                        + (eventInfo.event.extendedProps.type === 'other' ? ' bg-indigo-500' : ' bg-green-500')
                        + (eventInfo.event.extendedProps.type === 'other' ? ' border-indigo-500' : ' border-green-500')
                        + (eventInfo.event.extendedProps.type === 'other' ? ' shadow-custom-indigo' : ' shadow-custom-green')
                        + (eventInfo.event.extendedProps.type === 'other' ? ' text-indigo-500' : ' text-green-500')}
                    >
                        <b>{translations?.[eventInfo.event.title] ?? eventInfo.event.title}</b>
                    </div>
                );
            };

            return !hidden && (
                <div style={{width: '100%', height: '100%'}}>
                    <Calendar.StyleWrapper style={{width: '100%', height: '100%'}}>
                        <FullCalendar
                            height="100%"
                            ref={calendarRef}
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView={width > 756 ? 'timeGridWeek' : 'timeGridDay'}
                            headerToolbar={{
                                left: 'prev,next',
                                center: 'title',
                                right: '',
                            }}
                            views={{
                                timeGridWeek: true,
                                timeGridDay: true,
                            }}
                            events={events}
                            datesSet={handleDateSet}
                            eventClick={handleEventClick}
                            eventContent={renderEventContent}
                            locale={locale}
                            allDaySlot={false}
                            editable={false}
                            selectable={true}
                            selectMirror={true}
                            selectAllow={(selectInfo) => {
                                // Allow selection only if the start date is in the future
                                return selectInfo.start > new Date();
                            }}
                            select={handleDateSelect}
                            // Style for the selection
                            selectOverlap={false}
                            selectConstraint={{
                                startTime: '00:00',
                                endTime: '24:00',
                            }}
                        />
                    </Calendar.StyleWrapper>
                </div>
            );
        }
    }
}

Calendar.StyleWrapper = styled.div`
    .fc td, .fc th, .fc .fc-scrollgrid {
      border-color: #88888820 !important;
    }
  
    .fc-timegrid-slot-lane {
      cursor: pointer;
    }
  
    .fc-scrollgrid {
      background-color: white;
      border-radius: 5px;
    }
  
    .fc-col-header-cell {
      border-color: #88888820 !important;
    }
    .fc-day-today {
      background-color: transparent !important;
      color: inherit !important;
    }
    .fc-toolbar-title {
      font-size: 1.4em !important;
    }
    

    .fc-button {
      background-color: rgb(99 102 241 / 0.1) !important;
      color: #8b5cf6 !important;
      border: none !important;
        
    }
    .fc-button:hover {
      background-color: #6b7280 !important;
    },
  
    .fc-button-primary {
      background-color: rgb(99 102 241 / 0.1) !important;
      color: rgb(99 102 241 / 1) !important;
      
    }
    .fc-button-primary:hover {
      background-color: #9ca3af !important;
    }
    .fc-button-active, .fc-button-active:focus, .fc-prev-button:focus, .fc-next-button:focus {
      box-shadow: none !important;
    },

    .fc-event {
      background-color: transparent; !important;
      color: rgb(99 102 241 / 1) !important;
      border-color: transparent !important;
      //box-shadow: 0 0 5px rgb(99 102 241 / 1) !important;
    }
`

registerBrick('01932634856362fabf1d', Calendar);
